import { Injectable, signal, computed } from '@angular/core';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  /** ms antes de fechar automaticamente; 0 = não fecha automaticamente */
  duration: number;
  /** true quando animação de saída está em progresso */
  leaving: boolean;
}

export interface ToastOptions {
  message?: string;
  duration?: number;
}

@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly _toasts = signal<Toast[]>([]);

  /** Toasts visíveis (máx. 4) */
  readonly toasts = computed(() => this._toasts().slice(-4));

  success(title: string, opts?: ToastOptions): void {
    this._push({ type: 'success', title, ...opts });
  }

  error(title: string, opts?: ToastOptions): void {
    this._push({ type: 'error', title, duration: opts?.duration ?? 6000, message: opts?.message });
  }

  warning(title: string, opts?: ToastOptions): void {
    this._push({ type: 'warning', title, ...opts });
  }

  info(title: string, opts?: ToastOptions): void {
    this._push({ type: 'info', title, ...opts });
  }

  dismiss(id: string): void {
    // Marca como "leaving" para disparar animação CSS antes de remover
    this._toasts.update(list =>
      list.map(t => t.id === id ? { ...t, leaving: true } : t)
    );
    setTimeout(() => {
      this._toasts.update(list => list.filter(t => t.id !== id));
    }, 180);
  }

  private _push(partial: Omit<Toast, 'id' | 'leaving' | 'duration'> & { duration?: number }): void {
    const toast: Toast = {
      id:       crypto.randomUUID(),
      leaving:  false,
      duration: partial.duration ?? 4000,
      ...partial,
    };

    this._toasts.update(list => [...list, toast]);

    if (toast.duration > 0) {
      setTimeout(() => this.dismiss(toast.id), toast.duration);
    }
  }
}
